// Extracted audit table components for AuditLogPage.

import type { JSX } from "react";
import { formatGenericRef } from "../lib/refs.js";

const RESOURCE_PREFIX: Record<string, string> = {
  patient:         "P",
  service_request: "SR",
  carer:           "C",
  agency:          "A",
  agent:           "A",
};

export interface AuditEntry {
  _id: string;
  created_at: string;
  actor_id: string;
  actor_role: string;
  action: string;
  resource_type: string;
  resource_id: string;
  new_value?: Record<string, unknown> | null;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function newValuePills(nv: Record<string, unknown> | null | undefined): JSX.Element {
  if (!nv) return <span className="text-slate-400">—</span>;
  const keys = Object.keys(nv).slice(0, 3);
  if (keys.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {keys.map((k) => (
        <span key={k} className="inline-block bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 rounded font-mono">
          {k}
        </span>
      ))}
      {Object.keys(nv).length > 3 && (
        <span className="inline-block bg-slate-100 text-slate-400 text-[10px] px-1.5 py-0.5 rounded">+{Object.keys(nv).length - 3}</span>
      )}
    </span>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }): JSX.Element {
  return (
    <tr className="border-b border-slate-50 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
      <td className="py-2 pr-4 text-slate-500 whitespace-nowrap text-xs">{formatDate(entry.created_at)}</td>
      <td className="py-2 pr-4">
        <span className="font-mono text-xs text-slate-600">{formatGenericRef(entry.actor_id, "U")}</span>
        <span className="ml-1.5 inline-block bg-navy-50 text-navy-800 text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize">
          {entry.actor_role}
        </span>
      </td>
      <td className="py-2 pr-4 font-mono text-xs text-slate-800">{entry.action}</td>
      <td className="py-2 pr-4 text-xs">
        <span className="capitalize text-slate-600">{entry.resource_type.replace(/_/g, " ")}</span>
        <span className="ml-1 font-mono text-slate-400">{formatGenericRef(entry.resource_id, RESOURCE_PREFIX[entry.resource_type] ?? "REF")}</span>
      </td>
      <td className="py-2">{newValuePills(entry.new_value)}</td>
    </tr>
  );
}

export function AuditTablePanel({ isLoading, isError, items }: { isLoading: boolean; isError: boolean; items: AuditEntry[] }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4 overflow-x-auto">
      {isLoading && <p className="py-8 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>}
      {isError   && <p role="alert" className="py-4 text-center text-sm text-red-600">Failed to load audit records.</p>}
      {!isLoading && !isError && items.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No audit records found</p>}
      {!isLoading && !isError && items.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
              <th className="py-2 pr-4 font-medium">Timestamp</th>
              <th className="py-2 pr-4 font-medium">Actor</th>
              <th className="py-2 pr-4 font-medium">Action</th>
              <th className="py-2 pr-4 font-medium">Resource</th>
              <th className="py-2 font-medium">Details</th>
            </tr>
          </thead>
          <tbody>{items.map((e) => <AuditRow key={e._id} entry={e} />)}</tbody>
        </table>
      )}
    </div>
  );
}
